import jwtDefaultConfig from '@/auth/jwt/jwtDefaultConfig'

const PORTAL_USER_KEY = 'portalUserData'
const PORTAL_TOKEN_KEY = 'portalAccessToken'

// Filtros temporários da sessão interna devem ser removidos ao voltar para o login,
// evitando que IDs salvos em um tenant sejam reaproveitados em outro. Para incluir
// novos painéis nessa limpeza, adicione aqui a chave usada para memorizar seus filtros.
const INTERNAL_FILTER_STORAGE_KEYS = [
  'honorarium:painel-contrato:filtros:v2',
  'honorarium:painel-proposta:filtros:v1',
]

const getPortalUserRaw = () => sessionStorage.getItem(PORTAL_USER_KEY)
const getPortalTokenRaw = () => sessionStorage.getItem(PORTAL_TOKEN_KEY)

export const clearPortalSession = () => {
  sessionStorage.removeItem(PORTAL_USER_KEY)
  sessionStorage.removeItem(PORTAL_TOKEN_KEY)
}

export const persistPortalSession = ({ userData, accessToken }) => {
  if (userData) {
    sessionStorage.setItem(PORTAL_USER_KEY, JSON.stringify(userData))
  }
  if (accessToken) {
    sessionStorage.setItem(PORTAL_TOKEN_KEY, accessToken)
  }
}

export const getPortalToken = () => getPortalTokenRaw()

export const getUserData = () => {
  const portalRaw = getPortalUserRaw()
  if (portalRaw) {
    try {
      return JSON.parse(portalRaw)
    } catch (err) {
      // Caso os dados tenham sido corrompidos limpamos a sessão do portal
      clearPortalSession()
    }
  }
  const internalRaw = localStorage.getItem('userData')
  return internalRaw ? JSON.parse(internalRaw) : null
}

const clearInternalFilterStorage = () => {
  INTERNAL_FILTER_STORAGE_KEYS.forEach(key => {
    sessionStorage.removeItem(key)
    localStorage.removeItem(key)
  })
}

export const clearLoggedIn = () => {
  document.querySelector(':root').style.setProperty('--primary', '#c1af1d')
  document.querySelector(':root').style.setProperty('--primary-rgb', '193, 175, 29')
  localStorage.removeItem('userData')
  localStorage.removeItem(jwtDefaultConfig.storageTokenKeyName)
  clearInternalFilterStorage()
  clearPortalSession()
}

const LAST_LOGIN_EMAIL_KEY = 'honorarium:last-login-email'

export const getLastLoginEmail = () => localStorage.getItem(LAST_LOGIN_EMAIL_KEY) || ''

export const setLastLoginEmail = email => {
  const normalized = (email || '').trim()
  if (!normalized) return
  localStorage.setItem(LAST_LOGIN_EMAIL_KEY, normalized)
}

/**
 * This function is used for demo purpose route navigation
 * In real app you won't need this function because your app will navigate to same route for each users regardless of ability
 * Please note role field is just for showing purpose it's not used by anything in frontend
 * We are checking role just for ease
 * NOTE: If you have different pages to navigate based on user ability then this function can be useful. However, you need to update it.
 * @param {String} userRole Role of user
 */
export const getHomeRouteForLoggedInUser = userRole => {
  if (userRole === 'admin') return '/'
  if (userRole === 'client') return { name: 'access-control' }
  return { name: 'auth-login' }
}

export const isExternalAccess = () => {
  const data = getUserData()
  return data && data.code === 2
}

export const isAccessNotAuthenticated = () => {
  const data = getUserData()
  return data && data.code === 3
}

export const getUserInfo = () => {
  const data = getUserData()
  return data ? {
    nome: data.fullName,
    grupo: data.grupo,
    email: data.email,
    id: data.id,
  } : {}
}

export const getModulos = () => {
  const data = getUserData()
  return data ? data.modulos : []
}

export const getAbilitys = () => {
  const data = getUserData()
  return data ? data.ability : []
}

export const isGoingToLogging = (from, to) => {
  const fromDiffPaths = from.name !== to.name
  return fromDiffPaths && (to.path.startsWith('/login') || to.path.startsWith('/portal-cliente/login'))
}

export const isGoingToHome = (from, to) => {
  const fromDiffPaths = from.name !== to.name
  return fromDiffPaths && (to.path === '/')
}

export const isAdmin = () => {
  const userData = getUserData()
  return userData && userData.admin
}

/**
 * Return if user is logged in
 * This is completely up to you and how you want to store the token in your frontend application
 * e.g. If you are using cookies to store the application please update this function
 */
// eslint-disable-next-line arrow-body-style
export const isUserLoggedIn = route => {
  const internalLogged = localStorage.getItem('userData') && localStorage.getItem(jwtDefaultConfig.storageTokenKeyName)
  const portalLogged = getPortalUserRaw() && getPortalTokenRaw()
  const logged = internalLogged || portalLogged
  if (route === 'portal-cliente') {
    return logged && isExternalAccess()
  }
  return logged
}

export const updateAbility = (ability, abilityOverride = null) => {
  const baseAbility = abilityOverride || (getUserData() ? getUserData().ability : null)
  ability.update(baseAbility || [])
}

export const isModulo = () => {
  const modulos = getModulos()
  if (modulos && modulos[0] === 'Comercial') {
    return 'painel-proposta'
  }
  return 'portaldocumentos-inerno'
}

export const acessoModuloLink = item => {
  if (isAdmin()) {
    return true
  }

  if (isExternalAccess()) {
    return true
  }

  const abilities = (getUserData()?.ability || []).filter(f => f.subject === item.resource)
  if (!abilities.length) {
    return false
  }

  const moduloSelecionado = localStorage.getItem('moduloSelecionado')
  if (abilities.some(ability => ability.modulo === moduloSelecionado)) {
    return true
  }

  return true
}

export const acessoPropostaCartao = () => {
  if (isAdmin()) {
    return true
  }

  const ability = getUserData().ability.some(som => som.subject === 'proposta' && som.actions.some(so => so === 'read'))
  return ability
}

export const acessoModuloGrupo = item => {
  if (isAdmin()) {
    return true
  }

  const abilityList = getUserData()?.ability || []
  const moduloSelecionado = localStorage.getItem('moduloSelecionado')

  return item.children.some(child => {
    if (Array.isArray(child.children) && child.children.length) {
      return acessoModuloGrupo(child)
    }
    if (!child.resource) {
      return false
    }
    const matches = abilityList.filter(ability => ability.subject === child.resource)
    if (!matches.length) {
      return false
    }
    if (matches.some(ability => ability.modulo === moduloSelecionado)) {
      return true
    }
    return true
  })
}

export const permitidoAcessarRecurso = subject => {
  const abilitys = getAbilitys()
  const ability = abilitys.find(find => find.subject === subject)
  return ability
}

export const permitidoAcessarRecursoSeNaoAdmin = (subsject, permisao) => {
  let newPermisao = false
  if (!getUserData().admin) {
    const ability = permitidoAcessarRecurso(subsject)
    newPermisao = ability.actions.some(som => som === permisao)
  }
  return newPermisao
}

export const canDownloadECnpj = () => {
  const data = getUserData()
  return data && data.permitirDownloadA1Cnpj
}

export const canDownloadECPF = () => {
  const data = getUserData()
  return data && data.permititDownloadA1Cpf
}
